import {
  FloatingPortal,
  Placement,
  ReferenceType,
  autoUpdate,
  flip,
  offset,
  shift,
  useDelayGroupContext,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { useQuery } from "@tanstack/react-query";
import { GameData, select } from "gamedata/GameData";
import { ReactNode, useState } from "react";
import invariant from "tiny-invariant";
import { GameVer, Realm, tt, tturl, useRealm, useT } from "~/util/Lang";
import { UrlQuery, makeUrl } from "~/util/UrlUtils";
import { ItemInfoBox } from "../item/ItemInfoBox";
import { npcTitleColor } from "../npc/NpcLink";
import { RaceLink } from "../npc/RaceLink";
import { SkillInfoBox } from "../skill/SkillInfoBox";
import { Facts } from "../ui/Facts";
import css from "./Tooltip.module.css";

export type What =
  | { t: "item"; uname: string }
  | { t: "npc"; uname: string }
  | { t: "skill"; uname: string };

export function EntityTooltip(props: {
  gver?: GameVer;
  what: What;
  placement?: Placement;
  force?: boolean | undefined;
  children: (opts: {
    setReference: (node: ReferenceType | null) => void;
    getReferenceProps: (
      userProps?: React.HTMLProps<Element>
    ) => Record<string, unknown>;
  }) => ReactNode;
  onOpenChange?: (open: boolean) => void;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const open = props.force ?? showTooltip;
  const { refs, context, floatingStyles } = useFloating({
    open,
    onOpenChange: (newOpen) => {
      setShowTooltip(newOpen);
      props.onOpenChange?.(newOpen);
    },
    placement: props.placement ?? "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift()],
  });

  const { delay } = useDelayGroupContext();
  const hover = useHover(context, { delay, enabled: props.force == null });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  return (
    <>
      {props.children({ setReference: refs.setReference, getReferenceProps })}
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <TooltipContent what={props.what} gver={props.gver} />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

function TooltipContent(props: { what: What; gver?: GameVer }) {
  const what = props.what;
  const uname = what.uname;
  const realm = useRealm();
  const { data } = useQuery<TooltipData>(
    ["tooltip", what.t, uname],
    async () => {
      const search: UrlQuery = { _data: "" };
      switch (what.t) {
        case "item":
          search.item = what.uname;
          break;
        case "npc":
          search.npc = what.uname;
          break;
        case "skill":
          search.skill = what.uname;
          break;
      }
      const url = makeUrl({
        path: tturl(
          { lang: realm.lang, game: props.gver ?? realm.game },
          `/tooltip/item`
        ),
        search,
      });
      const r = await fetch(url);
      return await r.json();
    }
  );

  if (data == null) {
    return null;
  }

  switch (data.t) {
    case "item": {
      return <ItemToolip data={data} gver={props.gver} />;
    }
    case "npc": {
      return <NpcToolip data={data} gver={props.gver} />;
    }
    case "skill": {
      return <SkillToolip data={data} gver={props.gver} />;
    }
  }
}

function ItemToolip(props: {
  data: ReturnType<typeof selectItemData>;
  gver?: GameVer;
}) {
  const item = props.data.item;
  return (
    <div className={css.tooltipBox}>
      <ItemInfoBox item={item} gver={props.gver} tooltip={true} />
    </div>
  );
}

function NpcToolip(props: {
  data: ReturnType<typeof selectNpcData>;
  gver?: GameVer;
}) {
  const realm = useRealm();
  const t = useT();

  const npc = props.data.npc;
  return (
    <div className={css.tooltipBox}>
      <span
        style={{ display: "flex", flexDirection: "column", paddingBottom: 10 }}
      >
        {npc.nick && (
          <span
            style={{
              color: npcTitleColor({
                nickColor: npc.nickColor,
                type: npc.type,
              }),
              fontSize: 13,
              lineHeight: 1,
              paddingBottom: 4,
            }}
          >
            {t(npc.nick)}
          </span>
        )}
        <span style={{ color: "#fff", lineHeight: 1 }}>{t(npc.name)}</span>
      </span>

      <Facts
        facts={[
          {
            name: t({ en: "Aggr", ru: "Агро" }),
            value: (() => {
              return (
                <span style={{ color: npc.agro ? "red" : "green" }}>
                  {npc.agro
                    ? t({ en: "Aggressive", ru: "Агрессивный" })
                    : t({ en: "Passive", ru: "Пассивный" })}
                </span>
              );
            })(),
            show: npc.agro != null,
          },
          {
            name: "Англ. название",
            value: npc.name.en,
            show: realm.lang === "ru",
          },
          {
            name: t({
              en: "Level",
              ru: "Уровень",
            }),
            value: npc.level,
          },
          {
            name: t({
              en: "Race",
              ru: "Раса",
            }),
            value: npc.race && <RaceLink race={npc.race} />,
          },
          {
            name: t({
              en: "Reward",
              ru: "Награда",
            }),
            value: [[npc.exp, "EXP"] as const, [npc.sp, "SP"] as const]
              .filter(([v]) => !!v)
              .map(([v, u]) => `${Math.round(v ?? 0)} ${u}`)
              .join(", "),
            show: !!npc.exp || !!npc.sp,
          },
          {
            name: t({
              en: "HP / MP",
              ru: "ХП / МП",
            }),
            value: (
              <span>
                <span style={{ color: "red" }}>
                  {Math.round(npc.orgHp ?? 0)}
                </span>{" "}
                /{" "}
                <span style={{ color: "#008cff" }}>
                  {Math.round(npc.orgMp ?? 0)}
                </span>
              </span>
            ),
          },
          {
            name: t({
              en: "Location",
              ru: "Локация",
            }),
            value: npc.locs.map((loc, i) => (
              <span key={i}>
                <span>{loc.name}</span>
                {i < npc.locs.length - 1 ? ", " : ""}
              </span>
            )),
            show: npc.locs.length > 0,
          },
        ]}
      />
    </div>
  );
}

function SkillToolip(props: {
  data: ReturnType<typeof selectSkillData>;
  gver?: GameVer;
}) {
  const skill = props.data.skill;
  return (
    <div className={css.tooltipBox}>
      <SkillInfoBox skill={skill} gver={props.gver} />
    </div>
  );
}

export type TooltipData = ReturnType<typeof selectTooltipData>;

export function selectTooltipData(gd: GameData, realm: Realm, what: What) {
  switch (what.t) {
    case "item": {
      return selectItemData(gd, realm, what.uname);
    }
    case "npc": {
      return selectNpcData(gd, realm, what.uname);
    }
    case "skill": {
      return selectSkillData(gd, realm, what.uname);
    }
  }
}

function selectItemData(gd: GameData, realm: Realm, uname: string) {
  const item = gd.items.get(uname);
  invariant(item);

  const t = tt.bind(null, realm.lang);

  return {
    t: "item" as const,
    item: select(item, {
      id: true,
      itemName: true,
      name: { en: true, ru: true },
      addName: { en: true, ru: true },
      desc: t,
      icon: true,
      type: true,
      crystalType: true,
      crystalCount: true,
      slotBitType: true,
      weaponType: true,
      armorType: true,
      physicalDamage: true,
      magicalDamage: true,
      attackSpeed: true,
      shieldDefense: true,
      shieldDefenseRate: true,
      avoidModify: true,
      physicalDefense: true,
      magicalDefense: true,
      mpBonus: true,
      soulshotCount: true,
      spiritshotCount: true,
      defaultPrice: true,
      weight: true,
      durability: true,
    }),
  };
}

function selectNpcData(gd: GameData, realm: Realm, uname: string) {
  const npc = gd.npcs.get(uname);
  invariant(npc);

  const t = tt.bind(null, realm.lang);

  return {
    t: "npc" as const,
    npc: select(npc, {
      id: true,
      name: { en: true, ru: true },
      type: true,
      nick: { en: true, ru: true },
      nickColor: true,
      level: true,
      agro: true,
      agroRange: true,
      race: { name: t, icon: true },
      exp: true,
      sp: true,
      orgHp: true,
      orgMp: true,
      locs: { select: { name: t } },
    }),
  };
}

function selectSkillData(gd: GameData, realm: Realm, uname: string) {
  const [idStr, lvlStr] = uname.split("_");
  const skillCard = gd.skills.get(parseInt(idStr));
  invariant(skillCard);
  const skillLevel = skillCard.levels.get(parseInt(lvlStr));
  invariant(skillLevel);

  const t = tt.bind(null, realm.lang);

  return {
    t: "skill" as const,
    skill: select(skillLevel, {
      id: true,
      icon: true,
      name: { en: true, ru: true },
      desc: { en: true, ru: true },
      operateType: true,
    }),
  };
}
